import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import clsx from "clsx";
import AttributeIcon from "csi/middleware/attributes/AttributeIcon";
import { useDndStore } from "designSystem/dnd/index";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, useEffect, useRef } from "react";
import { FlexRowCenter, IconButton, OverflowText } from "src/designSystem/index";
import CustomViewAttribute from "./CustomViewAttribute";
import css from "./CustomViewAttributes.module.css";

interface AttributeSelectionProps {
	/** attribute to render */
	attribute: CustomViewAttribute;
}

const AttributeSelectionRemoveIcon: FunctionComponent<AttributeSelectionProps> = observer(function AttributeSelectionRemoveIcon({ attribute }) {
	const { onRemoveSelectedAttribute, store } = attribute;

	return store.hasMinimumNumberOfSelections ? null : (
		<IconButton
			design="iconOnly"
			onClick={onRemoveSelectedAttribute}
			round
			size="small"
		>
			<CloseIcon />
		</IconButton>
	);
});

const AttributeSelection: FunctionComponent<AttributeSelectionProps> = observer(function AttributeSelection({ attribute }) {
	const {
		container,
		hidden,
		id,
		isHighlighted,
		onClickHidden,
		onClickSelectedAttribute,
		resetScrollIntoViewFlag,
		scheduledForScrollIntoView,
		spec,
		store: { supportsVisibility },
	} = attribute;
	const { name } = spec;

	const { isDragging, dragItem } = useDndStore();

	const ref = useRef<HTMLFieldSetElement>(null);

	useEffect(() => {
		if (scheduledForScrollIntoView) {
			ref.current.scrollIntoView();
			resetScrollIntoViewFlag();
		}
	}, [scheduledForScrollIntoView]);

	const isFieldSetHighlighted = (isDragging && dragItem.id === attribute.id) || isHighlighted;

	return (
		/** make a cut in the border for the text with fieldset & legend */
		<fieldset
			className={clsx(css.selection, css.attribute, isFieldSetHighlighted && css.blueBackground)}
			data-csi-selection-attribute={id}
			onClick={onClickSelectedAttribute}
			ref={ref}
		>
			<legend>
				<OverflowText maxWidth={200} text={container.name} />
			</legend>
			<FlexRowCenter gap={2}>
				<AttributeIcon spec={spec} />
				<OverflowText
					grow
					lineHeight={14}
					semiBold
					text={name}
				/>
				{supportsVisibility && hidden && (
					<IconButton
						className={css.hiddenButton}
						design="iconOnly"
						onClick={onClickHidden}
						round
						size="small"
					>
						<VisibilityOffIcon />
					</IconButton>
				)}
				{supportsVisibility && (
					<IconButton
						className={css.toggleHiddenButton}
						design="iconOnly"
						onClick={onClickHidden}
						round
						size="small"
					>
						{hidden ? <VisibilityOffIcon /> : <VisibilityIcon />}
					</IconButton>
				)}
				<AttributeSelectionRemoveIcon attribute={attribute} />
			</FlexRowCenter>
		</fieldset>
	);
});

export default AttributeSelection;